import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { Button } from '@cms/components/data/Button'
import { SystemPopover } from '@cms/components/data/Popover'
import { AddToCollectionIcon } from '@cms/components/icons'
import { Loader } from '@cms/components/ui/Loader'
import { SystemTooltip } from '@cms/components/ui/Tooltip'
import { useSnackbar } from '@cms/providers/SnackbarProvider'
import api from '@cms/utils/api'
import { CollectionsPath, Routes } from '@cms/utils/routes'
import { useAppSelector } from '@cms/utils/store/hooks'
import { groupBy, IDropdownOptionV2, ISystemCollectionItem } from '@dl/declarations'
import { StoryFormData } from '../../../utils/forms/story'
import { generateSearchParam, mapLookupsToOptions } from '../../../utils/helpers'
import { collectionsLookupsQuery } from '../../../utils/queries'

import { FaExternalLinkAlt, FaRegCheckSquare, FaRegPlusSquare } from 'react-icons/fa'

export const AddToCollectionMenu = () => {
    const { showSnackbar } = useSnackbar()
    const { story, mode } = useAppSelector((state) => state.editor)
    const { watch, setValue } = useFormContext<StoryFormData>()
    const [selected_subtype, selected_collections] = watch(['subtype', 'collections'])

    // const selected_subtype_slug = useMemo(() => Object.values(SUBTYPES).find((subtype) => subtype.ID === selected_subtype)?.SLUG, [selected_subtype])

    const { data: allCollections, isLoading: isCollectionsLookupLoading } = collectionsLookupsQuery({
        collection_type: "Story",
        // subtypes: selected_subtype_slug || '',
    })

    const toggleCollectionMutation = useMutation({
        mutationFn: async (collectionId: string) => {
            if (!story?._id) return []
            const response = await api.put<Array<ISystemCollectionItem>>(`/stories/${story!._id}/collections/${collectionId}`)
            return response.data
        },
        onSuccess: (data) => {
            const updatedCollectionIds = data.map((collection) => collection.collection_ref)
            setValue('collections', updatedCollectionIds, {
                shouldTouch: false,
                shouldDirty: false,
                shouldValidate: false
            })
            showSnackbar('Story added to collection successfully', 'success')
        },
        onError: () => {
            showSnackbar('Failed to add story to collection', 'error')
        }
    })

    const handleClickOption = (collection: IDropdownOptionV2) => {
        toggleCollectionMutation.mutate(collection._id)
    }

    const renderOption = (collection: IDropdownOptionV2) => {
        const isSelected = (selected_collections || []).includes(collection._id)

        return (
            <button
                key={collection._id}
                className={clsx(
                    'flex items-center gap-2 rounded border cursor-pointer px-2 py-4 bg-white',
                    isSelected ? 'border-cs-normal-blue' : 'border-cs-dark-gray'
                )}
                onClick={() => handleClickOption(collection)}
                disabled={toggleCollectionMutation.isPending}
            >
                <div className='w-5'>
                    {isSelected ? (
                        <FaRegCheckSquare
                            size={24}
                            className={clsx('text-cs-normal-blue')}
                        />
                    ) : (
                        <FaRegPlusSquare
                            size={24}
                            className={clsx('text-cs-dark-gray')}
                        />
                    )}
                </div>

                <p
                    title={collection.label}
                    className={clsx(
                        'px-2 me-auto text-nowrap truncate ...',
                        isSelected ? 'border-cs-normal-blue text-cs-normal-blue' : 'border-cs-dark-gray text-cs-dark-gray'
                    )}
                >
                    {collection.label}
                </p>

                <SystemTooltip
                    message='Go to collection'
                >
                    <Link
                        target='_blank'
                        to={{
                            pathname: Routes.COLLECTIONS,
                            search: `?${generateSearchParam(CollectionsPath, "mode", "update")}&${generateSearchParam(CollectionsPath, "id", collection._id)}`

                        }}
                        onClick={(e) => {
                            e.stopPropagation()
                        }}
                    >
                        <FaExternalLinkAlt />
                    </Link>
                </SystemTooltip>
            </button>
        )
    }

    /**
    * 1. Map collections to options
    * 2. Group options by their groupKey
    */
    const collectionsLookupsToOptions = useMemo(() => {
        const mapped = mapLookupsToOptions(allCollections || [], 'name', (item) => {
            return item?.subtype
        })

        return groupBy(mapped, (item) => item.groupKey);
    }, [allCollections])

    const buttonState = useMemo(() => {
        if (!story?._id) {
            return {
                type: 'disabled',
                tooltip: 'Save before adding to a collection'
            }
        }

        if (mode !== 'editing') {
            return {
                type: 'disabled',
                tooltip: 'Story is in readonly mode'
            }
        }

        return {
            type: 'enabled',
            tooltip: 'Add to collection'
        }
    }, [mode, story])

    return (
        <SystemPopover
            trigger={{
                component: (
                    <Button
                        as='div'
                        shape='square'
                        tooltip={buttonState?.tooltip}
                        inputVariant='gray_text_white_bg'
                        disabled={buttonState?.type === 'disabled'}
                        indicator={selected_collections.length > 0 ? `${selected_collections.length}` : undefined}
                    >
                        <AddToCollectionIcon width="24" height="24" />
                    </Button>
                ),
                disabled: buttonState?.type === 'disabled',
            }}
            content={{
                component: (
                    <div
                        className='p-4 w-fit lg:w-[500px] xl:w-[600px]'
                    >
                        {(isCollectionsLookupLoading) ? (
                            <Loader loaderSize='sm' />
                        ) : (
                            <>
                                {collectionsLookupsToOptions[selected_subtype] ? (
                                    <div
                                        className={clsx(
                                            'grid grid-cols-2 gap-2',
                                            toggleCollectionMutation.isPending && 'cursor-progress *:cursor-progress'
                                        )}
                                    >
                                        {collectionsLookupsToOptions[selected_subtype].map((option) => renderOption(option))}
                                    </div>
                                ) : (
                                    <p>No collections available.</p>
                                )}
                            </>
                        )}
                    </div>
                )
            }}
        />
    )
}